import { Ban, CircleFadingPlus, FilePlus, Stamp, X } from "lucide-react";
import { FC, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { useListDocuments } from "~/api/default/default";
import { useGetVolumes } from "~/api/folders/folders";
import { DocumentStatus, FolderChatStatus } from "~/api/model";
import { Button } from "~/components/ui/button";
import { useProjectParams } from "~/helpers";
import { useToast } from "~/hooks/use-toast";
import { cn } from "~/lib/utils";
import {
  isBatesEligible,
  useApplyBates,
  useIsBatesEnabled,
  useUpdateBatesStatus,
} from "../../hooks/useBatesActions";
import { useDocumentSelectionStore } from "../../stores/documentSelectionStore";
import { AddToVersionModal } from "../AddToVersionDialog";
import { CreateVersionModal } from "../CreateVersionDialog";
import { ApplyBatesConfirmDialog } from "./ApplyBatesConfirmDialog";

export const DocumentsPanel: FC = () => {
  const {
    getSelectedCount,
    isSelectionMode,
    clearSelection,
    selectedDocumentIds,
  } = useDocumentSelectionStore();
  const [open, setOpen] = useState(false);
  const { formatMessage } = useIntl();
  const { toast } = useToast();
  const [openAddToVersionModal, setOpenAddToVersionModal] = useState(false);
  const [showApplyBatesDialog, setShowApplyBatesDialog] = useState(false);
  const isBatesEnabled = useIsBatesEnabled();
  const { mutateAsync: applyBates, isPending: isApplyingBates } =
    useApplyBates();
  const { mutateAsync: updateBatesStatus, isPending: isSkippingBates } =
    useUpdateBatesStatus();
  const { organizationId, caseId } = useProjectParams();
  const { data: documentsList } = useListDocuments(organizationId, caseId);
  const { data: volumes } = useGetVolumes(organizationId, caseId);

  // Get count of complete documents
  const completeDocumentsCount = documentsList?.documents
    ? selectedDocumentIds.filter((id) => {
        const document = documentsList.documents?.find((doc) => doc.id === id);
        return document?.status === DocumentStatus.COMPLETE;
      }).length
    : 0;

  const batesEligibleDocumentIds = useMemo(
    () =>
      selectedDocumentIds.filter((id) => {
        const document = documentsList?.documents?.find((doc) => doc.id === id);
        return document !== undefined && isBatesEligible(document);
      }),
    [selectedDocumentIds, documentsList]
  );

  const totalDocuments = documentsList?.documents?.length || 0;

  const allDocumentsSelected =
    getSelectedCount() === totalDocuments && totalDocuments > 0;

  const hasVersions = useMemo(() => {
    if (!volumes) return false;

    // If we are in the master, show button if there is at least one complete version
    if (volumes.some((v) => v.id === caseId && v.parentId === null)) {
      return volumes.some(
        (volume) =>
          volume.parentId !== null &&
          volume.chatStatus === FolderChatStatus.COMPLETE
      );
    }

    // If we are in the version, show button if there is at least one other complete version
    return volumes.some(
      (volume) =>
        volume.parentId !== null &&
        volume.id !== caseId &&
        volume.chatStatus === FolderChatStatus.COMPLETE
    );
  }, [volumes, caseId]);

  const numberOfFilesSelected = getSelectedCount();

  const handleConfirmApplyBates = async () => {
    try {
      await applyBates(batesEligibleDocumentIds);
      toast({
        title: formatMessage({
          id: "bates.applySuccess.title",
          defaultMessage: "Bates Numbers Applied",
        }),
        description: formatMessage({
          id: "bates.applySuccess.description",
          defaultMessage: "Stamping in progress — numbers will appear shortly.",
        }),
      });
      clearSelection();
    } catch {
      toast({
        variant: "destructive",
        title: formatMessage({
          id: "bates.applyError.title",
          defaultMessage: "Failed to Apply Bates Numbers",
        }),
        description: formatMessage({
          id: "bates.applyError.description",
          defaultMessage:
            "Some documents may already be stamped or still processing.",
        }),
      });
    }
  };

  return (
    <>
      <div
        className={cn(
          "fixed flex gap-2 bottom-0 left-1/2 -translate-x-1/2 h-auto p-[8px] pl-[16px] rounded-xl shadow-lg shadow-black/10 justify-center items-center mb-4 flex-col z-10",
          {
            "bg-background w-auto text-muted-foreground pointer-events-none":
              !isSelectionMode,
            "bg-blue-600 text-white w-auto pointer-events-none":
              isSelectionMode,
          }
        )}
      >
        <div className="flex flex-wrap gap-2 items-center">
          <span className="text-sm font-medium leading-tight inline-block ml-[8px] mr-[12px]">
            {numberOfFilesSelected}{" "}
            {formatMessage({
              id: numberOfFilesSelected > 1 ? "filesSelected" : "fileSelected",
            })}
            {completeDocumentsCount < numberOfFilesSelected &&
              ` (${completeDocumentsCount} ${formatMessage(
                { id: "processable" },
                { count: completeDocumentsCount }
              )})`}
          </span>
          {isBatesEnabled && batesEligibleDocumentIds.length > 0 && (
            <>
              <Button
                className="bg-blue-600 text-white pointer-events-auto border border-blue-500"
                onClick={() => setShowApplyBatesDialog(true)}
                disabled={isApplyingBates || isSkippingBates}
              >
                <Stamp className="w-4 h-4 mr-2" />
                <FormattedMessage
                  id="bates.applyBates"
                  defaultMessage="Apply Bates"
                />
              </Button>
              <Button
                className="bg-blue-600 text-white pointer-events-auto border border-blue-500"
                onClick={async () => {
                  try {
                    await updateBatesStatus({
                      documentIds: batesEligibleDocumentIds,
                      status: "SKIPPED",
                    });
                    clearSelection();
                  } catch {
                    toast({
                      variant: "destructive",
                      title: formatMessage({
                        id: "bates.actionError.title",
                        defaultMessage: "Bates Action Failed",
                      }),
                      description: formatMessage({
                        id: "bates.actionError.description",
                        defaultMessage:
                          "Could not update Bates status. Please try again.",
                      }),
                    });
                  }
                }}
                disabled={isApplyingBates || isSkippingBates}
              >
                <Ban className="w-4 h-4 mr-2" />
                <FormattedMessage id="bates.skip" defaultMessage="Skip Bates" />
              </Button>
            </>
          )}
          {hasVersions && !allDocumentsSelected && (
            <Button
              className="bg-blue-600 text-white pointer-events-auto border border-blue-500"
              onClick={() => setOpenAddToVersionModal(true)}
              disabled={completeDocumentsCount === 0}
            >
              <FilePlus className="w-4 h-4 mr-2" />
              <FormattedMessage
                id="addToVersion"
                defaultMessage="Add to Version"
              />
            </Button>
          )}
          <Button
            className="bg-white text-blue-600 pointer-events-auto hover:bg-blue-100 border border-blue-600"
            onClick={() => setOpen(true)}
            disabled={completeDocumentsCount === 0}
          >
            <CircleFadingPlus className="w-4 h-4 mr-2" />
            <FormattedMessage
              id="createNewVersion"
              defaultMessage="Create New Version"
            />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="bg-blue-600 hover:bg-blue-700 border-transparent !text-white pointer-events-auto border-none"
            onClick={clearSelection}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
        {completeDocumentsCount < getSelectedCount() && (
          <p className="text-xs text-white opacity-80 pb-1">
            Note: Only processed files will be included in the new version
          </p>
        )}
      </div>

      <CreateVersionModal open={open} setOpen={setOpen} />
      <AddToVersionModal
        open={openAddToVersionModal}
        setOpen={setOpenAddToVersionModal}
      />
      {showApplyBatesDialog && (
        <ApplyBatesConfirmDialog
          open={showApplyBatesDialog}
          onOpenChange={setShowApplyBatesDialog}
          documentCount={batesEligibleDocumentIds.length}
          onConfirm={handleConfirmApplyBates}
        />
      )}
    </>
  );
};
